import React from "react";
import { Link } from "react-router-dom";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
const INTER = { fontFamily: "'Inter', sans-serif" };
const C = {
  blue: "#0065F8",
  blueLight: "#0A7DFF",
  blueDeep: "#073B8E",
  cyan: "#00CAFF",
  cream: "#F5FAFF",
  cream2: "#EAF3FF",
  navy: "#0B1A33",
  slate: "#334155",
  slate2: "#475569",
  border: "rgba(11,26,51,0.08)",
};

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span
      style={{
        flexShrink: 0,
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: C.cream2,
        border: `2px solid rgba(0,101,248,0.18)`,
        color: C.blue,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1,
      }}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
    <div style={{ color: C.slate, fontSize: 15, lineHeight: 1.6 }}>{children}</div>
  </li>
);

const SectionHeading = ({ children }) => (
  <h2
    style={{
      ...FREDOKA,
      color: C.navy,
      fontWeight: 700,
      fontSize: "clamp(1.35rem, 2.5vw, 1.6rem)",
      letterSpacing: "-0.01em",
      margin: "0 0 12px",
    }}
  >
    {children}
  </h2>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${C.cream} 0%, ${C.cream2} 100%)`, ...INTER }}>
      <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-14 max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="inline-block mb-4"
            style={{
              ...FREDOKA,
              background: "#FFFFFF",
              border: `2px solid rgba(0,101,248,0.18)`,
              color: C.blue,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.18em",
              padding: "6px 14px",
              borderRadius: 999,
              textTransform: "uppercase",
            }}
          >
            Privacy Policy
          </div>
          <h1
            style={{
              ...FREDOKA,
              color: C.navy,
              fontWeight: 700,
              fontSize: "clamp(2rem, 5vw, 3rem)",
              letterSpacing: "-0.02em",
              lineHeight: 1.05,
              margin: 0,
            }}
          >
            We care about your <span style={{ color: C.blue }}>safety</span> and your trust.
          </h1>
          <p className="mx-auto mt-4" style={{ color: C.slate, fontSize: "clamp(0.95rem, 1.6vw, 1.1rem)", maxWidth: 560, lineHeight: 1.55 }}>
            This page explains how we handle your information.
          </p>
        </div>

        {/* Back button */}
        <div className="text-center mb-8">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 transition-all duration-200"
            style={{
              ...FREDOKA,
              background: "#FFFFFF",
              color: C.blue,
              fontWeight: 800,
              fontSize: 13,
              padding: "10px 18px",
              borderRadius: 999,
              border: `2px solid ${C.border}`,
              boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>

        {/* Main content card */}
        <div
          style={{
            background: "#FFFFFF",
            border: `2px solid ${C.border}`,
            borderRadius: 24,
            boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.06)",
            padding: "clamp(24px, 4vw, 40px)",
          }}
        >
          {/* Effective date pill */}
          <div className="text-center mb-8 pb-6" style={{ borderBottom: `2px solid ${C.cream2}` }}>
            <span
              style={{
                ...FREDOKA,
                background: C.cream2,
                color: C.blue,
                fontSize: 12,
                fontWeight: 700,
                padding: "6px 14px",
                borderRadius: 999,
                border: `1.5px solid rgba(0,101,248,0.15)`,
              }}
            >
              Effective Date: September 9, 2025
            </span>
          </div>

          {/* Intro */}
          <section className="mb-8">
            <p style={{ color: C.slate, fontSize: "clamp(0.95rem, 1.5vw, 1.05rem)", lineHeight: 1.65 }}>
              At Pixley ("we," "us," "our"), we value your trust and are committed to protecting your privacy. This Privacy Policy outlines how we collect, use, and protect your personal information, including content uploaded to our platform for the creation of custom children's shows and episodes.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <SectionHeading>Information We Collect</SectionHeading>
            <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.6, margin: "0 0 14px" }}>
              When you use our services, we collect the following information:
            </p>
            <ul className="space-y-3">
              <Bullet>
                <strong style={{ color: C.navy }}>Account Information:</strong> Your name, email address, and account credentials to provide access to our platform.
              </Bullet>
              <Bullet>
                <strong style={{ color: C.navy }}>Content Creation Data:</strong> Show descriptions, character information, episode themes, and story descriptions you provide to generate custom content.
              </Bullet>
              <Bullet>
                <strong style={{ color: C.navy }}>Payment Information:</strong> Billing details and payment information to process subscription and service fees.
              </Bullet>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <SectionHeading>How We Use Your Information</SectionHeading>
            <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.6, margin: "0 0 14px" }}>
              We use the information provided to:
            </p>
            <ul className="space-y-3">
              <Bullet>Generate and deliver custom children's shows and episodes as requested by you.</Bullet>
              <Bullet>Communicate with you about your account, content creation, and related inquiries.</Bullet>
              <Bullet>Provide customer support and technical assistance.</Bullet>
              <Bullet>Process payments and manage subscription services.</Bullet>
            </ul>
          </section>

          {/* Content Storage and AI Training */}
          <section className="mb-8">
            <SectionHeading>Content Storage and AI Training</SectionHeading>
            <ul className="space-y-3">
              <Bullet>Your content and generated shows are stored securely as long as you maintain an account.</Bullet>
              <Bullet>Content you create is used solely for generating your custom shows.</Bullet>
              <Bullet>You can request deletion of your content at any time through your account settings or by contacting us.</Bullet>
            </ul>
          </section>

          {/* Third-Party Sharing */}
          <section className="mb-8">
            <SectionHeading>Third-Party Sharing</SectionHeading>
            <ul className="space-y-3">
              <Bullet>We do not sell, license, or share your personal content with third parties for their commercial use.</Bullet>
              <Bullet>We may share information with trusted service providers (payment processors, cloud storage) necessary to operate our platform.</Bullet>
              <Bullet>We may disclose information if required by law or to protect our rights and safety.</Bullet>
            </ul>
          </section>

          {/* Children's Privacy — callout */}
          <section className="mb-8">
            <SectionHeading>Children's Privacy</SectionHeading>
            <div
              style={{
                background: C.cream2,
                border: `2px solid rgba(0,101,248,0.18)`,
                borderRadius: 18,
                padding: "20px 22px",
                boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
              }}
            >
              <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.6, margin: "0 0 12px" }}>
                <strong style={{ color: C.navy }}>Parental/Guardian Responsibility:</strong> If you create content featuring or intended for children, you affirm that you are the parent, legal guardian, or have obtained necessary permissions.
              </p>
              <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.6, margin: "0 0 12px" }}>
                Our platform is designed to create educational and entertaining content for children, and we take special care to ensure all generated content is appropriate and safe.
              </p>
              <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.6, margin: 0 }}>
                We do not knowingly collect personal information from children under 13 without parental consent.
              </p>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <SectionHeading>Data Security</SectionHeading>
            <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.6, margin: "0 0 14px" }}>
              We implement industry-standard measures to protect your data, including:
            </p>
            <ul className="space-y-3">
              <Bullet>Encryption of all data during transit and storage.</Bullet>
              <Bullet>Secure payment processing through trusted third-party providers.</Bullet>
              <Bullet>Regular security audits and monitoring to maintain data protection.</Bullet>
              <Bullet>Access controls and authentication to prevent unauthorized access.</Bullet>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <SectionHeading>Your Rights</SectionHeading>
            <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.6, margin: "0 0 14px" }}>
              You have the right to:
            </p>
            <ul className="space-y-3">
              <Bullet>Request correction of inaccurate personal information.</Bullet>
              <Bullet>Request deletion of your account and associated data.</Bullet>
              <Bullet>Object to certain uses of your information.</Bullet>
              <Bullet>Contact us to inquire about how your data is used and processed.</Bullet>
            </ul>
          </section>

          {/* Contact Us */}
          <section className="mb-8">
            <SectionHeading>Contact Us</SectionHeading>
            <div
              style={{
                background: C.cream,
                border: `2px solid ${C.border}`,
                borderRadius: 18,
                padding: "20px 22px",
                boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
              }}
            >
              <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.6, margin: "0 0 10px" }}>
                If you have questions or concerns about this Privacy Policy or how we handle your data, please contact us:
              </p>
              <p style={{ color: C.slate, fontSize: 15, margin: "0 0 6px" }}>
                <strong style={{ color: C.navy }}>Email:</strong>{" "}
                <a
                  href="mailto:team@pixleyai.com"
                  style={{ color: C.blue, fontWeight: 700, textDecoration: "none" }}
                  className="hover:underline"
                >
                  team@pixleyai.com
                </a>
              </p>
              <p style={{ color: C.slate, fontSize: 15, margin: 0 }}>
                <strong style={{ color: C.navy }}>Support:</strong> Use our in-app support feature or contact form
              </p>
            </div>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <SectionHeading>Changes to This Privacy Policy</SectionHeading>
            <p style={{ color: C.slate, fontSize: 15, lineHeight: 1.65 }}>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. Any changes will be posted on this page with an updated effective date. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
            </p>
          </section>

          {/* Agreement footer */}
          <section className="pt-6" style={{ borderTop: `2px solid ${C.cream2}` }}>
            <div
              className="text-center"
              style={{
                background: `linear-gradient(180deg, ${C.cream} 0%, ${C.cream2} 100%)`,
                border: `2px solid rgba(0,101,248,0.18)`,
                borderRadius: 20,
                padding: "24px 22px",
                boxShadow: "0px 3px 0px rgba(11,26,51,0.05)",
              }}
            >
              <p style={{ ...FREDOKA, color: C.navy, fontSize: 17, fontWeight: 700, margin: "0 0 8px", lineHeight: 1.35 }}>
                By using our services, you agree to the terms of this Privacy Policy.
              </p>
              <p style={{ color: C.slate2, fontSize: 14, margin: 0 }}>
                If you do not agree, please refrain from using our platform.
              </p>
            </div>
          </section>
        </div>

        {/* Back-to-home link */}
        <div className="text-center mt-8">
          <Link
            to="/"
            style={{ ...FREDOKA, color: C.blue, fontWeight: 700, fontSize: 13, letterSpacing: "0.04em", textTransform: "uppercase", textDecoration: "none" }}
            className="hover:underline"
          >
            ← Back to Pixley
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
